import axios from 'axios';
import type { Track } from '../types/Track';

// Module-held API key configured at runtime (loaded from Firestore via the app)
let RUNTIME_YOUTUBE_API_KEY: string | undefined;

export const setYoutubeApiKey = (key: string | undefined) => {
  RUNTIME_YOUTUBE_API_KEY = key?.trim() || undefined;
};

export const hasYoutubeApiKey = (): boolean => Boolean(RUNTIME_YOUTUBE_API_KEY);

const http = axios.create({
  baseURL: 'https://www.googleapis.com/youtube/v3',
  timeout: 15000,
});

// --- Lightweight, browser-only API usage tracking (localStorage) ---
export type ApiUsage = {
  date: string; // YYYY-MM-DD (local)
  requests: number;
  estimatedUnits: number;
  byEndpoint: Record<string, { requests: number; units: number }>;
};

const USAGE_LS_KEY = 'ytApiUsage.v1';
const todayKey = () => new Date().toISOString().slice(0, 10);

const readUsage = (): ApiUsage => {
  try {
    const raw = localStorage.getItem(USAGE_LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as ApiUsage;
      if (parsed?.date === todayKey()) return parsed;
    }
  } catch {}
  return { date: todayKey(), requests: 0, estimatedUnits: 0, byEndpoint: {} };
};

const writeUsage = (u: ApiUsage) => {
  try { localStorage.setItem(USAGE_LS_KEY, JSON.stringify(u)); } catch {}
};

const incrementUsage = (endpoint: string, units: number) => {
  const u = readUsage();
  u.requests += 1;
  u.estimatedUnits += units;
  const ep = u.byEndpoint[endpoint] || { requests: 0, units: 0 };
  ep.requests += 1;
  ep.units += units;
  u.byEndpoint[endpoint] = ep;
  writeUsage(u);
};

export const getApiUsage = (): ApiUsage => readUsage();
export const resetApiUsage = () => writeUsage({ date: todayKey(), requests: 0, estimatedUnits: 0, byEndpoint: {} });

type SearchItem = {
  id: { videoId: string };
  snippet: {
    title: string;
    channelTitle: string;
    thumbnails: { default?: { url: string }; medium?: { url: string } };
    liveBroadcastContent?: 'none' | 'live' | 'upcoming';
  };
};

type VideosItem = {
  id: string;
  status?: { embeddable?: boolean; privacyStatus?: string };
  contentDetails?: {
    contentRating?: { ytRating?: string };
    regionRestriction?: { blocked?: string[]; allowed?: string[] };
  };
  snippet?: {
    thumbnails?: {
      default?: { url?: string };
      medium?: { url?: string };
      high?: { url?: string };
      standard?: { url?: string };
      maxres?: { url?: string };
    };
  };
  statistics?: { viewCount?: string; likeCount?: string };
};

export const searchMusicVideos = async (query: string): Promise<Track[]> => {
  if (!query.trim()) return [];
  const API_KEY = RUNTIME_YOUTUBE_API_KEY;
  if (!API_KEY) {
    throw new Error('Missing YouTube API key. Add one in Settings.');
  }

  // 1) Basic search for music videos that are embeddable and not live
  const searchRes = await http.get<{ items: SearchItem[] }>('/search', {
    params: {
      key: API_KEY,
      part: 'snippet',
      q: query,
      type: 'video',
      maxResults: 12,
      videoEmbeddable: 'true',
      videoCategoryId: '10', // Music category
      safeSearch: 'moderate',
      regionCode: 'US',
      fields:
        'items(id/videoId,snippet(title,channelTitle,thumbnails/default,thumbnails/medium,liveBroadcastContent))',
    },
  });
  // Default YouTube Data API cost assumption: search.list = 100 units
  incrementUsage('search.list', 100);

  const candidates = (searchRes.data.items || []).filter(
    (it) => it.snippet.liveBroadcastContent === 'none'
  );
  const ids = candidates.map((c) => c.id.videoId).filter(Boolean);
  if (ids.length === 0) return [];

  // 2) Filter by status/details to avoid age-restricted or non-embeddable videos
  const videosRes = await http.get<{ items: VideosItem[] }>('/videos', {
    params: {
      key: API_KEY,
      part: 'status,contentDetails,snippet,statistics',
      id: ids.join(','),
      fields:
        'items(id,status/embeddable,status/privacyStatus,contentDetails/contentRating/ytRating,contentDetails/regionRestriction,snippet/thumbnails/default/url,snippet/thumbnails/medium/url,snippet/thumbnails/high/url,snippet/thumbnails/standard/url,snippet/thumbnails/maxres/url,statistics/viewCount,statistics/likeCount)',
    },
  });
  // Default YouTube Data API cost assumption: videos.list = 1 unit
  incrementUsage('videos.list', 1);

  const videosById = new Map(videosRes.data.items?.map((v) => [v.id, v] as const));

  const playable = candidates.filter((c) => {
    const v = videosById.get(c.id.videoId);
    if (!v) return false;
    if (v.status?.privacyStatus !== 'public') return false;
    if (v.status?.embeddable === false) return false;
    const ytAge = v.contentDetails?.contentRating?.ytRating;
    if (ytAge === 'ytAgeRestricted') return false;
    const blocked = v.contentDetails?.regionRestriction?.blocked || [];
    if (blocked.includes('US')) return false;
    return true;
  });

  const tracks: Track[] = playable.map((it) => {
    const v = videosById.get(it.id.videoId);
    const maxres = v?.snippet?.thumbnails?.maxres?.url
      || v?.snippet?.thumbnails?.standard?.url
      || v?.snippet?.thumbnails?.high?.url
      || it.snippet.thumbnails.medium?.url
      || it.snippet.thumbnails.default?.url
      || '';
    const small = it.snippet.thumbnails.medium?.url || it.snippet.thumbnails.default?.url || maxres || '';
    const viewCount = v?.statistics?.viewCount ? Number(v.statistics.viewCount) : undefined;
    const likeCount = v?.statistics?.likeCount ? Number(v.statistics.likeCount) : undefined;
    const track: Track = {
      videoId: it.id.videoId,
      title: it.snippet.title,
      channelTitle: it.snippet.channelTitle,
      thumbnailUrl: small,
      maxresThumbnailUrl: maxres || undefined,
      viewCount,
      likeCount,
    };
    return track;
  });

  return tracks;
};
